"use client";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaPinterest,
  FaYoutube,
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
} from "react-icons/fa";

interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social?: string;
}

interface CategoryLink {
  id: string;
  name: string;
  slug: string;
}

interface Address {
  id: number;
  text: string;
  address: string;
  country: string;
  phone?: string;
  email?: string;
}

function RenderSocialIcon({ social }: { social: string | undefined }) {
  const iconClass = "text-lg hover:text-blue-400 transition-colors duration-300";

  switch (social) {
    case "TWITTER":
      return <FaTwitter className={iconClass} />;
    case "INSTAGRAM":
      return <FaInstagram className={iconClass} />;
    case "PINTEREST":
      return <FaPinterest className={iconClass} />;
    case "FACEBOOK":
      return <FaFacebookF className={iconClass} />;
    case "YOUTUBE":
      return <FaYoutube className={iconClass} />;
    case "LINKEDIN":
      return <FaLinkedinIn className={iconClass} />;
    default:
      return null;
  }
}

export default function Footer_Layout5({
  logoUrl,
  logoText,
  menuLinks,
  productLinks,
  categoryLinks,
  legalLinks,
  socialLinks,
  addresses,
  images,
}: {
  logoUrl: string | null;
  logoText: string | null;
  menuLinks: Array<FooterLink>;
  productLinks: Array<CategoryLink>;
  categoryLinks: Array<CategoryLink>;
  legalLinks: Array<FooterLink>;
  socialLinks: Array<FooterLink>;
  addresses: Array<Address>;
  images?: any;
}) {
  const bgUrl = images && Array.isArray(images) && images.length > 0 ? images[0]?.url : null;

  return (
    <footer className="relative bg-white text-gray-800 overflow-hidden border-t border-gray-200">
      <div className="container mx-auto px-5 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          {/* Brand & Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="col-span-1 md:col-span-4 space-y-5"
          >
            {logoUrl && (
              <div className="mb-6">
                <img
                  src={logoUrl}
                  alt={logoText || "Logo"}
                  className="h-12 object-contain"
                />
              </div>
            )}
            
            {logoText && (
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                {logoText}
              </p>
            )}
            
            <div className="flex gap-3">
              {socialLinks.map((link) => (
                <motion.a
                  key={link.id}
                  href={link.url}
                  target={link.newTab ? "_blank" : "_self"}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200 hover:bg-gray-200 transition-colors duration-300"
                  aria-label={`Follow us on ${link.social}`}
                >
                  <RenderSocialIcon social={link.social} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Product Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="col-span-1 md:col-span-2 space-y-5"
          >
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">Products</h3>
            <ul className="space-y-3">
              {productLinks?.map((link, index) => (
                <motion.li
                  key={link.id}
                  initial={{ x: -5 }}
                  whileInView={{ x: 0 }}
                  transition={{ delay: 0.15 + index * 0.03 }}
                  viewport={{ once: true }}
                >
                  <a
                    href={`/products/${link.slug}`}
                    className="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-300 flex items-center gap-2"
                  >
                    <FaArrowRight className="text-xs opacity-70" />
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="col-span-1 md:col-span-2 space-y-5"
          >
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">Company</h3>
            <ul className="space-y-3">
              {menuLinks.map((link, index) => (
                <motion.li
                  key={link.id}
                  initial={{ x: -5 }}
                  whileInView={{ x: 0 }}
                  transition={{ delay: 0.15 + index * 0.03 }}
                  viewport={{ once: true }}
                >
                  <a
                    href={link.url}
                    target={link.newTab ? "_blank" : "_self"}
                    className="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-300 flex items-center gap-2"
                  >
                    <FaArrowRight className="text-xs opacity-70" />
                    {link.text}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact & Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            viewport={{ once: true, margin: "0px" }}
            className="col-span-1 md:col-span-4 space-y-5"
          >
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">Get in Touch</h3>
            
            {addresses && addresses.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="text-gray-500 mt-1" />
                  <p className="text-gray-600 text-sm">
                    {addresses[0].address}, {addresses[0].country}
                  </p>
                </div>
                
                {addresses[0].phone && (
                  <div className="flex items-center gap-3">
                    <FaPhone className="text-gray-500" />
                    <a href={`tel:${addresses[0].phone}`} className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                      {addresses[0].phone}
                    </a>
                  </div>
                )}
                
                {addresses[0].email && (
                  <div className="flex items-center gap-3">
                    <FaEnvelope className="text-gray-500" />
                    <a href={`mailto:${addresses[0].email}`} className="text-gray-600 hover:text-gray-900 text-sm transition-colors">
                      {addresses[0].email}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {/* Newsletter Subscription */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Subscribe to our newsletter</h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Email address"
                  className="flex-1 bg-gray-100 border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-md text-sm font-medium transition-colors duration-300 whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            viewport={{ once: true, margin: "0px" }}
            className="text-xs text-gray-500"
          >
            © {new Date().getFullYear()} {logoText || "Company"}. All rights reserved.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            viewport={{ once: true, margin: "0px" }}
            className="flex flex-wrap justify-center gap-5"
          >
            {legalLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target={link.newTab ? "_blank" : "_self"}
                whileHover={{ color: "#2563eb" }}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                {link.text}
              </motion.a>
            ))}
          </motion.div>
        </div>
      </div>
    </footer>
  );
}